import { Chip, ChipProps, Stack, styled, Theme } from '@mui/material';
import { FC } from 'react';

import { CrossIcon } from '../../assets/icons';

declare module '@mui/material/Chip' {
  interface ChipPropsSizeOverrides {
    large: true;
  }
}

const SIZE_CONFIG = {
  small: { height: 20, typography: 'body1' as const, deleteIcon: 11, deleteWrapper: 14 },
  medium: { height: 26, typography: 'h3' as const, deleteIcon: 18, deleteWrapper: 22 },
  large: { height: 30, typography: 'h3' as const, deleteIcon: 18, deleteWrapper: 22 },
};

export type BubbleTone =
  | 'default'
  | 'default-inverse'
  | 'error'
  | 'error-inverse'
  | 'gray'
  | 'gray-inverse'
  | 'muted';
export type BubbleSize = keyof typeof SIZE_CONFIG;

export type BubbleProps = Omit<ChipProps, 'color' | 'variant' | 'size'> & {
  tone?: BubbleTone;
  size?: BubbleSize;
  disableHover?: boolean;
};

type ToneTokens = {
  text: string;
  bg: string;
  border: string;
  hoverBg?: string;
  hoverBorder?: string;
  icon: string;
  iconHover: string;
};

const toToneStyle = (t: ToneTokens, options?: { disableHover?: boolean }) => {
  const baseStyles = {
    '&.MuiChip-colorDefault': {
      color: t.text,
      backgroundColor: t.bg,
      borderColor: t.border,
    },
    '& .MuiChip-icon': { color: t.icon },
    '& .MuiChip-avatar': { color: t.icon },
    '& .MuiChip-deleteIcon path': { fill: t.icon },
  };

  if (options?.disableHover) return baseStyles;

  return {
    ...baseStyles,
    '&.MuiChip-colorDefault:not(.MuiChip-deletable):hover:not(.Mui-disabled)': {
      backgroundColor: t.hoverBg ?? t.bg,
      borderColor: t.hoverBorder ?? t.border,
    },
    '& .MuiChip-deleteIcon:hover path': { fill: t.iconHover },
  };
};

const buildVariants = (theme: Theme) => {
  const { palette } = theme;

  const toneDefinitions: Array<{ tone: BubbleTone; tokens: ToneTokens }> = [
    {
      tone: 'default',
      tokens: {
        text: palette.primary.main,
        bg: palette.common.white,
        border: palette.common.light2,
        hoverBorder: palette.common.grey,
        icon: palette.common.grey,
        iconHover: palette.primary.main,
      },
    },
    {
      tone: 'default-inverse',
      tokens: {
        text: palette.common.white,
        bg: palette.primary.main,
        border: palette.primary.main,
        hoverBg: palette.common.main2,
        hoverBorder: palette.common.main2,
        icon: palette.common.white,
        iconHover: palette.common.white,
      },
    },
    {
      tone: 'error',
      tokens: {
        text: palette.error.main,
        bg: palette.common.white,
        border: palette.error.main,
        icon: palette.error.main,
        iconHover: palette.error.main,
      },
    },
    {
      tone: 'error-inverse',
      tokens: {
        text: palette.error.main,
        bg: palette.error.superLight ?? 'transparent',
        border: palette.error.light,
        icon: palette.error.main,
        iconHover: palette.error.main,
      },
    },

    {
      tone: 'gray',
      tokens: {
        text: palette.common.grey2,
        bg: palette.common.white,
        border: palette.common.light2,
        hoverBg: palette.common.lightBg,
        hoverBorder: palette.common.grey,
        icon: palette.common.grey2,
        iconHover: palette.primary.main,
      },
    },
    {
      tone: 'gray-inverse',
      tokens: {
        text: palette.primary.main,
        bg: palette.common.lightBg,
        border: palette.common.main5,
        hoverBorder: palette.common.grey,
        icon: palette.common.grey,
        iconHover: palette.primary.main,
      },
    },
    {
      tone: 'muted',
      tokens: {
        text: palette.primary.main,
        bg: palette.common.white,
        border: palette.common.main5,
        hoverBg: palette.common.lightBg,
        hoverBorder: palette.common.main5,
        icon: palette.common.grey,
        iconHover: palette.common.grey,
      },
    },
  ];

  const toneVariants = toneDefinitions.flatMap(({ tone, tokens }) => [
    {
      props: { tone, disableHover: true } as const,
      style: toToneStyle(tokens, { disableHover: true }),
    },
    {
      props: { tone, disableHover: false } as const,
      style: toToneStyle(tokens),
    },
  ]);

  const sizeVariants = Object.entries(SIZE_CONFIG).map(([size, cfg]) => ({
    props: { size: size as BubbleSize },
    style: {
      height: cfg.height,
      '& .MuiChip-label': { ...theme.typography[cfg.typography], color: 'inherit' },
      '& .MuiChip-avatar': { width: cfg.height, height: cfg.height },
      '& .MuiChip-deleteIcon': {
        width: cfg.deleteWrapper,
        height: cfg.deleteWrapper,
        '& svg': { width: cfg.deleteIcon, height: cfg.deleteIcon },
      },
    },
  }));

  const hoverBaseVariants = [
    {
      props: { disableHover: false } as const,
      style: {
        '& .MuiChip-deleteIcon:hover': { backgroundColor: palette.common.light2 },
      },
    },
  ];

  return [...toneVariants, ...sizeVariants, ...hoverBaseVariants];
};

const BubbleRoot = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'tone' && prop !== 'disableHover',
})<{ tone: BubbleTone; disableHover?: boolean }>(({ theme }) => ({
  width: 'fit-content',
  maxWidth: '100%',
  borderStyle: 'solid',
  borderWidth: 1,
  transition: theme.transitions.create(['background-color', 'border-color', 'color'], {
    duration: theme.transitions.duration.shortest,
  }),

  '& .MuiChip-label': { paddingInline: 10, color: 'inherit' },
  '& .MuiChip-icon': { width: 12, height: 12 },
  '& .MuiChip-avatar': { marginLeft: -1, marginRight: 0 },

  variants: buildVariants(theme),
}));

const DeleteIconWrapper = styled(Stack)({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  cursor: 'pointer',
});

const defaultDeleteIcon = (
  <DeleteIconWrapper>
    <CrossIcon />
  </DeleteIconWrapper>
);

export const Bubble: FC<BubbleProps> = ({
  tone = 'default',
  size = 'small',
  deleteIcon,
  disableHover = false,
  ...props
}) => {
  return (
    <BubbleRoot
      tone={tone}
      size={size}
      disableHover={disableHover}
      color="default"
      deleteIcon={deleteIcon ?? defaultDeleteIcon}
      {...props}
    />
  );
};
